import Label from "@components/form/Label";
import SelectSubscriberExtractionPoint from "@components/form/SelectSubscriberExtractionPoint";
import TextInput from "@components/form/TextInput";
import MapView from "@components/shared/MapView";
import { useAppContext } from "@context/AppContext";
import { useMeterDeclarationContext } from "@context/MeterDeclarationContext";
import { formatDatetimeInput } from "@utils/formatDate";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { t as translate } from "i18next";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import SelectZenithLevel0Resource from "@components/form/SelectZenithLevelResources";
import StepActions from "@components/zenith/ui/StepActions";

type WorkflowWaterDeclarationStep1Props = {
  onSubmit: () => void;
};

const WorkflowWaterDeclarationStep1: FunctionComponent<
  WorkflowWaterDeclarationStep1Props
> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const { loggedInInfo, currentAccountingPeriod } = useAppContext();
  const navigate = useNavigate();
  const { declaration, setDeclaration } = useMeterDeclarationContext();
  const [errors, setErrors] = useState({
    zone: "",
    extractionPoint: "",
    meter: "",
    readAt: "",
  });

  const handleSelectZone = (e: any) => {
    setDeclaration({
      ...declaration,
      meter: null,
      extractionPoint: null,
      zone: {
        id: e?.value,
        identifier: e?.label,
      },
    });
  };

  const handleSelectExtractionPoint = (e: any) => {
    setDeclaration({
      ...declaration,
      meter: e?.data?.meter,
      extractionPoint: e?.data,
    });
  };

  const handleSelectMeter = (e: any) => {
    setDeclaration({
      ...declaration,
      meter: e?.data,
    });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!declaration?.extractionPoint?.id) {
      setErrors({
        ...errors,
        extractionPoint: translate(
          "zenith.water_declaration.errors.extraction_point_required",
        ),
      });
      return;
    } else if (isNaN(new Date(declaration?.readAt).getTime())) {
      setErrors({
        ...errors,
        readAt: translate("zenith.water_declaration.errors.read_at_invalid"),
      });
      return;
    }

    setErrors({
      zone: "",
      extractionPoint: "",
      meter: "",
      readAt: "",
    });

    onSubmit();
  };

  return (
    <form className="flex flex-col grow" onSubmit={handleSubmit}>
      <div className="flex grow gap-4">
        <fieldset className="w-1/2 flex flex-col gap-2">
          <div>
            <Label htmlFor="declaration_zone">{t("common.level0wrs")}</Label>
            <SelectZenithLevel0Resource
              inputId="declaration_zone"
              value={declaration?.zone?.id}
              onChange={handleSelectZone}
              errorMessage={errors?.zone}
              isClearable
            />
          </div>
          <div>
            <Label htmlFor="declaration_extraction_point">
              {t("common.extraction_point")}
            </Label>
            <SelectSubscriberExtractionPoint
              inputId="declaration_extraction_point"
              key={declaration?.zone?.id}
              definedByWalletId={loggedInInfo?.userDetails?.walletId}
              value={declaration?.extractionPoint?.id}
              onChange={handleSelectExtractionPoint}
              errorMessage={errors?.extractionPoint}
              level0ResourceId={declaration?.zone?.id}
              isClearable
            />
          </div>
          <div className="h-1/2">
            {declaration?.extractionPoint?.address?.location ? (
              <MapView
                center={{
                  lat: +declaration?.extractionPoint?.address?.location?.split(
                    ",",
                  )?.[0],
                  lng: +declaration?.extractionPoint?.address?.location?.split(
                    ",",
                  )?.[1],
                }}
                zoomLevel={15}
                markers={[
                  {
                    lat: +declaration?.extractionPoint?.address?.location?.split(
                      ",",
                    )?.[0],
                    lng: +declaration?.extractionPoint?.address?.location?.split(
                      ",",
                    )?.[1],
                  },
                ]}
              />
            ) : (
              declaration?.extractionPoint && (
                <p className="w-full text-center">
                  {t("zenith.water_declaration.no_location")}
                </p>
              )
            )}
          </div>
        </fieldset>
        <fieldset className="w-1/2 flex flex-col gap-2">
          <div>
            <Label htmlFor="declaration_meter">
              {t("zenith.water_declaration.linked_meter")}
            </Label>
            <Select
              inputId="declaration_meter"
              options={
                declaration?.extractionPoint?.meter
                  ? [
                      {
                        label: declaration?.extractionPoint?.meter?.serialNo,
                        value: declaration?.extractionPoint?.meter?.id,
                        data: declaration?.extractionPoint?.meter,
                      },
                    ]
                  : []
              }
              value={
                declaration?.meter?.id
                  ? {
                      label: declaration?.extractionPoint?.meter?.serialNo,
                      value: declaration?.extractionPoint?.meter?.id,
                      data: declaration?.extractionPoint?.meter,
                    }
                  : null
              }
              onChange={handleSelectMeter}
            />
          </div>
          <div>
            <Label htmlFor="readAt">
              {t("zenith.water_declaration.read_at")}
            </Label>
            <TextInput
              id="readAt"
              type="datetime-local"
              required
              value={
                declaration?.readAt && formatDatetimeInput(declaration?.readAt)
              }
              onChange={e => {
                const readAt = new Date(e.target.value);

                if (!isNaN(readAt.getTime()))
                  setDeclaration({
                    ...declaration,
                    readAt: new Date(e.target.value),
                  });
              }}
              min={
                currentAccountingPeriod?.periodStart
                  ? formatDatetimeInput(
                      new Date(currentAccountingPeriod?.periodStart),
                    )
                  : undefined
              }
              max={
                currentAccountingPeriod?.periodEnd
                  ? formatDatetimeInput(
                      new Date(currentAccountingPeriod?.periodEnd),
                    )
                  : undefined
              }
              errorMessage={errors?.readAt}
            />
          </div>
        </fieldset>
      </div>
      <StepActions
        onCancel={() => navigate("/zenith/workflows")}
        nextDisabled={
          !declaration?.extractionPoint?.id || !declaration?.meter?.id
        }
      />
    </form>
  );
};

export default WorkflowWaterDeclarationStep1;
